/* Home hero video controller — pause/play only, no native controls.
   Handles desktop (16:9) and mobile (9:16) videos: only the visible
   one plays; the toggle syncs both.

   Viewport height is PURE CSS (100dvh + -webkit-fill-available) — no JS
   tracking. This avoids the jitter / "patched" look that --app-vh caused
   on iOS (resize events fire on every toolbar slide, forcing relayouts).
   bfcache stale paint is fixed by forceRepaint() on pageshow. */
(function () {
  'use strict';

  var BREAKPOINT = window.matchMedia('(min-width: 768px)');

  function forceRepaint() {
    document.documentElement.style.setProperty('--force-reflow', '1');
    void document.documentElement.offsetHeight;
    document.documentElement.style.removeProperty('--force-reflow');
  }

  /* bfcache restores a frozen paint — force a full relayout/repaint
     so 100dvh re-evaluates with the current viewport. */
  window.addEventListener('pageshow', function (event) {
    if (event.persisted) {
      forceRepaint();
      requestAnimationFrame(forceRepaint);
    }
  });
  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'visible') {
      forceRepaint();
    }
  });

  function isDesktopElement(video) {
    return video.classList.contains('home-hero__media-element--desktop');
  }

  function initHeroMedia(section) {
    var toggle = section.querySelector('[data-hero-video-toggle]');
    var videos = Array.prototype.slice.call(section.querySelectorAll('[data-hero-video]'));
    if (!toggle || videos.length === 0 || toggle.dataset.heroBound) return;
    toggle.dataset.heroBound = '1';

    var motionOk = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var userPaused = false;

    function setPaused(paused) {
      toggle.classList.toggle('is-playing', !paused);
      /* Placeholder img covers the video whenever nothing is playing
         (initial load, user pause, iOS tab-switch suspension). */
      section.classList.toggle('is-hero-playing', !paused);
      toggle.setAttribute(
        'aria-label',
        paused ? toggle.dataset.labelPlay : toggle.dataset.labelPause
      );
    }

    function anyPlaying() {
      return videos.some(function (v) { return !v.paused && !v.ended; });
    }

    function syncState() {
      var playing = anyPlaying();
      setPaused(!playing);
      return playing;
    }

    function play(video) {
      video.preload = 'auto';
      var p = video.play();
      if (p && typeof p.catch === 'function') p.catch(function () {});
    }

    /* Only the video matching the active breakpoint plays — avoids
       double bandwidth on phones (16:9 master) and desktops (9:16). */
    function applyBreakpoint() {
      if (!motionOk || videos.length < 2) {
        if (motionOk && !anyPlaying()) play(videos[0]);
        return;
      }
      var wantedDesktop = BREAKPOINT.matches;
      videos.forEach(function (v) {
        var isWanted = isDesktopElement(v) === wantedDesktop;
        if (!isWanted) {
          v.pause();
        } else if (!anyPlaying()) {
          play(v);
        }
      });
    }

    setPaused(!anyPlaying());

    if (!motionOk) {
      videos.forEach(function (v) { v.pause(); });
      setPaused(true);
    } else {
      applyBreakpoint();
    }

    toggle.addEventListener('click', function () {
      if (anyPlaying()) {
        userPaused = true;
        videos.forEach(function (v) { v.pause(); });
      } else {
        userPaused = false;
        videos.forEach(function (v) {
          if (videos.length < 2 || isDesktopElement(v) === BREAKPOINT.matches) {
            play(v);
          }
        });
      }
      syncState();
    });

    /* iOS suspends background-tab videos and clears their frame.
       Resume seamlessly when the user comes back. */
    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState !== 'visible') return;
      if (!motionOk || userPaused) return;
      applyBreakpoint();
      syncState();
    });

    videos.forEach(function (v) {
      v.addEventListener('play', function () { setPaused(false); });
      v.addEventListener('pause', function () {
        if (!v.ended) syncState();
      });
    });

    if (typeof BREAKPOINT.addEventListener === 'function') {
      BREAKPOINT.addEventListener('change', applyBreakpoint);
    } else if (typeof BREAKPOINT.addListener === 'function') {
      BREAKPOINT.addListener(applyBreakpoint);
    }
  }

  function initAll() {
    document.querySelectorAll('.home-hero').forEach(initHeroMedia);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (event) {
    var hero = event.target.querySelector('.home-hero');
    if (hero) initHeroMedia(hero);
  });
})();
